import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigatedAction } from '@ngrx/router-store';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { SegmentTrackEvent } from '@ninety/mobile/platform/services';

import { appActions } from '../app/app.actions';

import { SegmentActions } from './analytics/segment/segment.actions';
import { GlobalNavActions } from './router/global-nav.actions';
import { toGlobalFeatureKey } from './router/route.selectors';

/**
 * App-wide engagement analytics. Root-scoped (registered in the app-global effects) so these fire no
 * matter which page is loaded.
 */
@Injectable()
export class AppAnalyticsEffects {
  private readonly actions$ = inject(Actions);

  /**
   * Track when a user switches to a different company. `switchCompany` is dispatched from the
   * company selector, which is available across the app; the selector only dispatches on a real switch.
   */
  trackCompanySwitched$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.switchCompany),
      map(() => SegmentActions.track({ event: SegmentTrackEvent.COMPANY_SWITCHED }))
    )
  );

  /**
   * Track main-nav taps that actually take the user somewhere.
   *
   * A tap is only intent, so it is paired with the navigation that follows it. Route guards can turn
   * a tap around (`UserRoleIsVerifiedGuardFn` bounces the People tab to `/` below the `managee` role
   * floor) — a redirect lands on a different key and is dropped, and a guard that returns false
   * commits no navigation at all. `switchMap` retires a still-pending tap when a newer one arrives.
   */
  trackGlobalNavItemNavigated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalNavActions.navItemTapped),
      filter(({ wasSelected }) => !wasSelected),
      switchMap(({ featureKey }) =>
        this.actions$.pipe(
          ofType(routerNavigatedAction),
          take(1),
          map(({ payload }) => payload.routerState.url),
          filter(landedUrl => toGlobalFeatureKey(landedUrl) === featureKey),
          map(landedUrl =>
            SegmentActions.track({
              event: SegmentTrackEvent.GLOBAL_NAV_ITEM_NAVIGATED,
              params: {
                featureKey,
                destinationUrl: landedUrl,
              },
            })
          )
        )
      )
    )
  );
}
